<script>
import formatDate, { dateDiffInMinutes, formatLunar } from '@utils/format-date'
import { authComputed } from '@state/helpers'
import { parseStringToDateTime } from '@utils/parse-string-to-date'
import ModalDeduct from '@components/modal/ModalDeduct'
import ModalLogin from '@components/modal/ModalLogin'
import formatOrderStatus from '@utils/format-order-status'
import formatProductType from '@utils/format-product-type'
import formatOrderGoogleCalendarUrl from '@utils/format-order-google-calendar-url'

export default {
  filters: {
    formatDate,
    formatLunar
  },
  components: {
    ModalDeduct,
    ModalLogin
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    pageType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isDeductProcessing: false,
      isModalDeductActive: false,
      deductCallBack: null,
      isModalLoginActive: false,
      loginCallBack: null,
    }
  },
  computed: {
    ...authComputed,
    hasExtrafieldsBySize() {
      const customerInfos =
        this.order.customerInfos.length > 0 ? this.order.customerInfos : false;
      const orderExtrafields = customerInfos
        ? customerInfos[0].bySizeExtrafields
        : {};

      return Object.keys(orderExtrafields).length > 0
    },
    hasExtrafieldsByOrder() {
      const orderExtrafields = this.order.byOrderExtrafields || {};

      return Object.keys(orderExtrafields).length > 0
    },
    hasExtrafields() {
      return this.hasExtrafieldsBySize || this.hasExtrafieldsByOrder
    },
    newebpayCallBack() {
      const callbackData = window.globalData.newebpay;
      return (
        callbackData &&
        callbackData.status !== '{{$status}}' && {
          isSuccess: callbackData.status === 'SUCCESS',
          isExpired: callbackData.status === 'EXPIRED',
          message: callbackData.message || '',
        }
      )
    },
    orderCallBack() {
      const callbackData = window.globalData.order;
      return (
        callbackData &&
        callbackData.status !== '{{$status}}' && {
          isSuccess: callbackData.status === 'SUCCESS',
          message: callbackData.type || '',
        }
      )
    },
    greetingNameWithTitle() {
      const genderTitleMap = {
        male: this.$t('global.GREETING_MALE_WITH_NAME' /* 先生 */, {
          name: this.order.fullName,
        }),
        female: this.$t('global.GREETING_FEMALE_WITH_NAME' /* 小姐 */, {
          name: this.order.fullName,
        }),
      };
      return genderTitleMap[this.order.gender]
    },
    orderStatusClass() {
      const statusClassMap = {
        0: 'has-text-orange is-size-6',
        1: 'has-text-primary',
        2: 'has-text-danger is-size-6',
        3: 'has-text-primary',
        4: 'has-text-primary',
      };
      return statusClassMap[this.order.status]
    },
    orderTimeInterval() {
      const dateDiff = dateDiffInMinutes(
        this.order.startDate,
        this.order.endDate
      );

      if (dateDiff > 60 * 24 * 30) {
        return this.$tc(
          'global.ABOUT_TIME_MOUTH',
          dateDiff / (60 * 24 * 30),
          {
            minute: Math.floor(dateDiff / (60 * 24 * 30)),
          } /* (預計{minute}個月) */
        )
      } else if (dateDiff > 60 * 24) {
        return this.$tc(
          'global.ABOUT_TIME_DAY',
          dateDiff / (60 * 24),
          { minute: Math.floor(dateDiff / (60 * 24)) } /* (預計{minute}天) */
        )
      } else if (dateDiff > 60) {
        return this.$tc(
          'global.ABOUT_TIME_HOUR',
          dateDiff / 60,
          { minute: Math.floor(dateDiff / 60) } /* (預計{minute}小時) */
        )
      } else {
        return this.$tc(
          'global.ABOUT_TIME_MINUTE',
          dateDiff,
          { minute: Math.floor(dateDiff) } /* (預計{minute}分鐘) */
        )
      }
    },
    orderProductType() {
      if (this.pageType === 'prepaid') {
        return 'prepaid'
      } else {
        return this.order.productType || 'activity'
      }
    },
    isNullOrder() {
      return (
        typeof this.order === 'object' && Object.keys(this.order).length === 0
      )
    },
  },
  created() {
    this.$store.dispatch('booking/updateCart')
  },
  methods: {
    formatOrderStatus,
    formatProductType,
    formatOrderGoogleCalendarUrl,
    bankDeadline() {
      return this.order.paymentExpiredDate ? formatDate( this.order.paymentExpiredDate, 'dateTime') : '';
    },
    bankCodeText(bankCode) {
      const bankCodeMap = {
        '812': '台新銀行',
        '004': '台灣銀行',
        '008': '華南銀行',
      };

      return bankCodeMap[bankCode]
    },
    handleContact() {
      const vnodes = []

      if (this.order.merchant.email) {
        vnodes.push(
          <a href={`mailto:${this.order.merchant.email}`}>
            <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="far"
              data-icon="envelope"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              class="svg-inline--fa fa-envelope fa-fw icon is-box fix-position is-left"
            >
              <path
                fill="currentColor"
                d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z"
              ></path>
            </svg>
            {this.order.merchant.email}
          </a>
        )
      }

      if (this.order.merchant.phoneNumber) {
        vnodes.push(<br />)
        vnodes.push(
          <a href={`tel:${this.order.merchant.phoneNumber}`}>
            <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="fal"
              data-icon="mobile-android-alt"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 512"
              class="svg-inline--fa fa-mobile-android-alt fa-fw icon is-box fix-position is-left"
            >
              <path
                fill="currentColor"
                d="M224 96v240H96V96h128m48-96H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM48 480c-8.8 0-16-7.2-16-16V48c0-8.8 7.2-16 16-16h224c8.8 0 16 7.2 16 16v416c0 8.8-7.2 16-16 16H48zM244 64H76c-6.6 0-12 5.4-12 12v280c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12V76c0-6.6-5.4-12-12-12zm-48 352h-72c-6.6 0-12 5.4-12 12v8c0 6.6 5.4 12 12 12h72c6.6 0 12-5.4 12-12v-8c0-6.6-5.4-12-12-12z"
              ></path>
            </svg>
            {this.order.merchant.phoneNumber}
          </a>
        )
      }
      const createElement = this.$createElement
      const messageElement = createElement('p', null, vnodes)

      this.$alert(
        messageElement,
        this.$t(
          'global.CONTACT_PREFIX',
          {
            name: this.order.merchant.name,
          } /* 聯繫{merchantName} */
        ),
        {
          distinguishCancelAndClose: false,
          dangerouslyUseHTMLString: true,
          confirmButtonText: this.$t(
            'global.LIGHT_BOX_CANCEL_RESERVATION_CONFIRM' /* 我瞭解了 */
          ),
          confirmButtonClass: 'button is-primary is-rounded',
        }
      )
    },
    reloadPage() {
      this.$router.go(0)
    },
    toOrder(orderId, hasData = true) {
      if (!this.loggedIn && this.orderCallBack) {
        this.isModalLoginActive = true;
        this.loginCallBack = () => {
          this.toOrder(orderId, hasData)
        };
        return false
      }

      window.globalData.order = hasData
        ? {
            status: 'SUCCESS',
            type: 'deduct',
          }
        : false;

      if (
        hasData ||
        (this.orderCallBack && Object.keys(this.orderCallBack).length > 0)
      ) {
        this.reloadPage()
      } else {
        this.$router.push({
          name:
            this.pageType === 'prepaid'
              ? 'prepaid-order-detail'
              : 'order-detail',
          params: {
            merchantAlias: this.$route.params.merchantAlias,
            orderKey: orderId,
          },
          query: {
            o: this.$route.query.o ? undefined : orderId,
            language: this.$route.query.language,
          },
        })
      }
    },
    handleToOrderItem() {
      if (!this.loggedIn) {
        this.isModalLoginActive = true;
        this.loginCallBack = () => {
          this.handleToOrderItem()
        };
        return false
      }

      this.$router.push({
        name: 'member-order-item',
        params: {
          merchantAlias: this.order.merchant.alias,
          orderKey: this.order.id,
        },
        query: {
          language: this.$route.query.language,
        },
      })
    },
    orderIsCanPay(order) {
      const isExpired = parseStringToDateTime(order.paymentExpiredDate) <= new Date();

      return !order.paymentExpiredDate || !isExpired
    },
    formatImg(type) {
      switch (type) {
        case 'light':
          return require('@assets/images/icon-light.svg');
        case 'treasury':
          return require('@assets/images/icon-treasury.svg');
        case 'puja':
          return require('@assets/images/icon-puja.svg');
        case 'pudu':
          return require('@assets/images/icon-pudu.svg');
        case 'worshipset':
          return require('@assets/images/icon-worshipset.svg');
        default:
          return ''
      }
    },
    formatText(type) {
      switch (type) {
        case 'fullName':
          return this.order.gender
            ? this.greetingNameWithTitle
            : this.order.fullName;
        case 'orderDetail':
          return this.$t(`product.${this.orderProductType}.ORDER_DETAILS` /* 以下是您的結緣資訊 */);
        case 'productName':
          return this.order.productName;
        case 'light':
        case 'worshipset':
          return formatLunar(this.order.startDate);
        case 'treasury':
        case 'pudu':
          return formatDate(this.order.startDate, 'dateSlash');
        case 'puja':
          return `${formatDate(this.order.startDate, 'dateTime')}`;
        default:
          return ''
      }
    },
    formatLink(type) {
      switch (type) {
        case 'home':
          return {
            name: 'home',
            params: {
              merchantAlias: this.order.merchant.alias,
            },
            query: { language: this.$route.query.language },
          };
        case 'order-tickets':
          return {
            name: 'order-tickets',
            params: {
              orderKey: this.order.id,
            },
            query: { language: this.$route.query.language },
          };
        case 'order-codes':
          return {
            name: 'order-codes',
            params: {
              orderKey: this.order.id,
            },
            query: { language: this.$route.query.language },
          };
        case 'checkout-unpaid':
          return {
            name: 'checkout-unpaid',
            params: {
              merchantAlias: this.order.merchant.alias,
              productId: this.order.productAlias,
              orderKey: this.order.id,
            },
            query: { language: this.$route.query.language },
          };
        case 'booking-product':
          return {
            name: 'booking-product',
            params: {
              merchantAlias: this.order.merchant.alias,
              productType: this.order.productType,
              productId: this.order.productAlias,
            },
            query: { language: this.$route.query.language },
          };
        default:
          return ''
      }
    },
    buttonList(type) {
      switch (type) {
        case 'paid':
          return [
            {
              textFormat: () => {
                if (this.order.productType === 'light') {
                  return '查看我的燈'
                }
                if (this.order.productType === 'worshipset') {
                  return '查看我的斗燈'
                }
              },
              type: 'link',
              style: 'primary',
              if: this.formatProductType('category', this.order.productType) === 'light',
              to: this.formatLink('order-codes'),
              disabled: this.formatOrderStatus('alias', this.order.status) !== 'complete',
              meta: {
                disabledTip: '（待廟方人員完成安奉作業後即可查看）',
              },
            },
            {
              text: this.$t('global.ADMISSION_QRCODE' /* 使用 QR Code */ ),
              type: 'link',
              style: 'primary',
              if: this.formatProductType('category', this.order.productType) === 'activity',
              to: this.formatLink('order-tickets'),
            },
            {
              textFormat: () => {
                const needLoginText = !this.loggedIn
                  ? `(${this.$t('global.NEED_LOGIN' /* 需驗證 */)})`
                  : '';
                return (
                  this.$t(
                    'global.MEMBER_ORDER_VIEW_DETAIL' /* 查看結緣詳情 */
                  ) + needLoginText
                )
              },
              type: 'button',
              style: 'black',
              fn: () => {
                this.handleToOrderItem()
              },
            },
            {
              text: this.$t('global.ADD_CALENDAR'/* 加入行事曆 */),
              type: 'a',
              style: 'black',
              if: this.formatProductType('category', this.order.productType) === 'activity',
              to: this.formatOrderGoogleCalendarUrl(this.order)
            },
            {
              text: '聯繫我們',
              type: 'button',
              style: 'black',
              fn: () => {
                this.handleContact()
              },
            },
          ];
        case 'unPaid':
          return [
            {
              textFormat: () => {
                if(this.order.paymentMethod === 'Cash') {
                  return '無法付款（現金付款）'
                }
                if (!this.orderIsCanPay(this.order)) {
                  return '無法付款（超過付款期限）'
                }
              },
              type: 'button',
              style: 'primary',
              if: !this.orderIsCanPay(this.order) && (!this.order.paymentMethod || this.order.paymentMethod === 'Cash'),
              disabled: true
            },
            {
              text: this.$t('global.PAYMENT_NOW' /* 前往付款 */),
              type: 'link',
              style: 'primary',
              if: this.orderIsCanPay(this.order) && !this.order.paymentMethod,
              to: this.formatLink('checkout-unpaid'),
            },
            {
              textFormat: () => {
                const needLoginText = !this.loggedIn
                  ? `(${this.$t('global.NEED_LOGIN' /* 需驗證 */)})`
                  : '';
                return (
                  this.$t(
                    'global.MEMBER_ORDER_VIEW_DETAIL' /* 查看結緣詳情 */
                  ) + needLoginText
                )
              },
              type: 'button',
              style: 'black',
              fn: () => {
                this.handleToOrderItem()
              },
            },
          ];
        case 'cancel':
          return [
            {
              text: this.$t(
                `product.${this.orderProductType}.BUTTON_GO_TO_DETAIL` /* 查看資訊 */
              ),
              type: 'link',
              style: 'black',
              to: this.formatLink('booking-product'),
            },
            {
              textFormat: () => {
                const needLoginText = !this.loggedIn
                  ? `(${this.$t('global.NEED_LOGIN' /* 需驗證 */)})`
                  : '';
                return (
                  this.$t(
                    'global.MEMBER_ORDER_VIEW_DETAIL' /* 查看結緣詳情 */
                  ) + needLoginText
                )
              },
              type: 'button',
              style: 'black',
              fn: () => {
                this.handleToOrderItem()
              },
            },
          ];
        case 'outside':
          return [
            {
              text: this.$t(`global.BACK_TO_HOME` /* 回宮廟首頁 */),
              type: 'link',
              style: 'black',
              to: this.formatLink('home'),
            },
          ];
        default:
          return []
      }
    },
    payDue() {
      if(this.order.paymentMethod === 'Credit' && this.formatProductType('category', this.order.productType) === 'light') {
        return ''
      }
      else if (this.orderIsCanPay(this.order)) {
        return this.order.paymentExpiredDate ? this.$t('global.PAYMENT_DATE_UNPAID', { date: formatDate(this.order.paymentExpiredDate, 'dateTime') } /* 請於 {date} 前完成付款 */) : ''
      } else {
        return this.$t('global.PAYMENT_DATE_EXPIRED' /* 已超過付款期限 */)
      }
    },
    paidText() {
      return this.$t(`global.PAYMENT_METHOD_ORDER_${(this.order.paymentMethod || 'NULL').toUpperCase()}`, { date: formatDate(this.order.paidDate, 'dateWeekDay') }/* 信用卡付款 {date} */)
    },
    cancelText() {
      return this.$t('global.PAYMENT_DATE_CANCELED', { date: formatDate(this.order.cancelDate, 'dateWeekDay') }/* 於 {date} 取消 */)
    },
  },
}
</script>
<template>
  <div class="booking-container has-text-centered">
    <div class="booking-section--outside">
      <div class="is-size-5 has-text-grey-dark has-text-weight-medium">{{ formatText('fullName') }}</div>
      <div class="has-text-grey-light has-text-weight-medium">{{ formatText('orderDetail') }}</div>
    </div>
    <div class="booking-section">
      <div class="subsection" style="background: #f9f8f6;">
        <div class="subsection__inner" style="background: #fff;">
          <div class="subsection__inner-subsection">
            <div class="orderDatetime__img">
              <img alt="" :src="formatImg(orderProductType)" />
            </div>
            <h4 class="orderDatetime__name">{{ formatText('productName') }}</h4>
            <div class="orderDatetime">
              <div class="orderDatetime__date">{{ formatText(orderProductType) }}
                <br v-if="order.inventoryTitle">
                <span v-if="order.inventoryTitle && orderProductType === 'puja'">{{ order.inventoryTitle }}</span>
              </div>
            </div>
            <div :class="$style['paramsList']">
              <div
                v-for="(param) in order.params"
                :key="param.name"
                :class="$style['paramsList__item']"
              >
                <div>{{ param.name }}</div>
                <div>
                  <span class="has-text-grey-light param-multiplier">x</span>
                  <span class="has-text-weight-medium">{{ param.qty }}</span>
                </div>
              </div>
            </div>
            <div :class="$style['paramsList__total']">
              <div class="level has-text-left" :class="$style.orderTitle">
                <div class="level-left">
                    <div class="has-text-weight-medium" :class="orderStatusClass">
                      {{ formatOrderStatus('text', order.status, order.paymentMethod) }}
                    </div>
                </div>
                <div
                  class="level-right is-size-4 has-text-right"
                  :class="formatOrderStatus('alias', order.status) === 'cancel' ? 'has-text-grey-light' : 'has-text-primary'"
                >
                ${{ order.priceTotal }}
                </div>
              </div>
              <div>
                    <div
                      v-if="formatOrderStatus('paid', order.status)"
                      class="is-size-7 has-text-grey has-text-left"
                    >
                      {{ paidText() }}
                    </div>
                    <div v-if="formatOrderStatus('alias', order.status) === 'unpaid'" class="is-size-7 has-text-danger has-text-left">
                      {{ payDue() }}
                    </div>
                    <div v-if="formatOrderStatus('alias', order.status) === 'cancel'" class="is-size-7 has-text-grey-light has-text-left">
                      {{ cancelText() }}
                    </div>
              </div>
            </div>
          </div>
          <div
            v-if="order.paymentMethod === 'Vacc'"
            class="subsection__inner-subsection"
            :class="$style['atm']"
          >
            <p class="is-text-content">
              {{ $t('global.TRANSFER_BANK'/*轉帳銀行*/) }} ｜ {{ bankCodeText(order.BankCode) }} {{ order.BankCode }}
              <br />
              {{ $t('global.TRANSFER_ACCOUNT'/*轉帳帳號*/) }} ｜ {{ order.VACCNo }}
              <br />
              {{ $t('global.TRANSFER_DEADLINE'/*轉帳期限*/) }} ｜ {{ bankDeadline(order.created) }}
              <br />
              {{ $t('global.TRANSFER_AMOUNT'/*轉帳金額*/) }} ｜ ${{ order.priceTotal }} 元
            </p>
            <p
              style="color: #6E4302;font-size: 13px;line-height: 1.7;margin-top: 2rem;text-align: justify"
            >請您依照指定的帳號轉入款項，金額需相符不可溢繳 付款後約3~5個工作日可於會員中心查詢是否登記成功</p>
          </div>
          <div
            v-if="formatProductType('category', order.productType) === 'light'"
            class="subsection__inner-subsection"
            :class="$style['tip']"
          >
            <div :class="$style['title']">提醒您</div>
            <span class="is-text-content has-text-align-justify">
              <p>▌支付緣金後，約10個工作天，由廟方人員完成安奉作業。</p>
              <p>▌點燈完成後，即可查看專屬序號或圖片，可根據此於宮廟內找到您的燈。</p>
            </span>
          </div>
        </div>
      </div>
      <template>
        <div v-if="formatOrderStatus('paid', order.status)" class="subsection">
          <BaseButtons :buttons="buttonList('paid')" />
        </div>
        <div
          v-else-if="formatOrderStatus('alias', order.status) === 'unpaid'"
          class="subsection"
        >
          <BaseButtons :buttons="buttonList('unPaid')" />
        </div>
        <div v-else-if="formatOrderStatus('alias', order.status) === 'cancel'" class="subsection">
          <BaseButtons :buttons="buttonList('cancel')" />
        </div>
      </template>
    </div>
    <div class="booking-section--outside">
      <BaseButtons :buttons="buttonList('outside')" />
    </div>
    <b-modal :active.sync="isModalDeductActive" :width="370" :can-cancel="['escape','outside']">
      <ModalDeduct :order="order" @close="isModalDeductActive = false" @login="deductCallBack" />
    </b-modal>
    <b-modal
      custom-class="is-login-style"
      :active.sync="isModalLoginActive"
      :width="370"
      :can-cancel="['escape','outside']"
    >
      <ModalLogin @close="isModalLoginActive = false" @login="loginCallBack" />
    </b-modal>
  </div>
</template>
<style lang="scss" module>
.paramsList {
  max-width: 80vw;
  margin: 1rem auto;
  border-bottom: 1px dashed $color-primary;

  @include mobile-smaller {
    width: 100%;
    margin-left: 0;
  }
  &__item {
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    padding-bottom: 0.75rem;
    line-height: 25px;
    letter-spacing: 0.9px;
  }
  &__total {
    .orderTitle {
      align-items: baseline;
      margin-bottom: 0.3rem;

      @include mobile {
        display: flex;
        justify-content: space-between;
      }

      @include mobile-smaller {
        flex-wrap: wrap;
        width: 100%;
        margin-left: 0;
      }
    }
  }
}
.orderDatetime {
  &__time {
    @extend %typography-xxlarge;

    margin-bottom: 3rem;
    color: $color-primary;
  }
  &__name {
    margin-bottom: 1rem;
    font-size: 24px;
    font-weight: 500;
    color: #852426;
  }
  &__date {
    margin-bottom: 2rem;
    font-size: 21px;
    font-weight: 500;
    color: $color-text-primary;
  }
  &__total {
    padding-bottom: 5px;
  }
  &__img {
    width: 160px;
    margin: 0 auto 0.5rem;
  }
}
.tip {
  font-size: 14px;
  color: #222;
  text-align: left;
  .title {
    margin-bottom: 0.5rem;
    color: #852426;
  }
  p {
    margin-bottom: 0.5rem;
    font-size: 15px;
    line-height: 30px;
    color: #222;
    letter-spacing: 0.75px;
  }
}
.atm {
  text-align: left;
  font-size: 15px;
}
</style>
